<template>
    <div :style="{ height: `calc(100vh - ${store.headerHeight}px)` }">
        <ag-grid-vue
            :theme="myTheme"
            :grid-options
            :row-data
            :column-defs="colDefs"
            :style="{ height: '100%', width: '100%' }"
            @grid-ready="onGridReady"
        />
    </div>
</template>

<script lang="ts" setup generic="T">
import { AgGridVue } from "ag-grid-vue3"
import type { ColDef, GridApi, GridOptions } from "ag-grid-community"
import {
    AllCommunityModule,
    ModuleRegistry,
    themeAlpine,
    iconOverrides,
} from "ag-grid-community"
import { AG_GRID_LOCALE_CZ } from "@ag-grid-community/locale"
import type { TableData } from "@nuxt/ui"

ModuleRegistry.registerModules([AllCommunityModule])

const myTheme = themeAlpine.withPart(
    iconOverrides({
        type: "image",
        icons: {
            filter: { url: "https://api.iconify.design/prime:filter.svg" },
            next: { url: "https://api.iconify.design/prime:angle-right.svg" },
            previous: {
                url: "https://api.iconify.design/prime:angle-left.svg",
            },
            last: {
                url: "https://api.iconify.design/prime:angle-double-right.svg",
            },
            first: {
                url: "https://api.iconify.design/prime:angle-double-left.svg",
            },
            smallUp: { url: "https://api.iconify.design/prime:angle-up.svg" },
            smallDown: {
                url: "https://api.iconify.design/prime:angle-down.svg",
            },
            asc: { url: "https://api.iconify.design/prime:arrow-up.svg" },
            desc: { url: "https://api.iconify.design/prime:arrow-down.svg" },
        },
    })
)

const store = useMainStore()

interface Props {
    loading: boolean
    rowData: TableData[]
    colDefs: ColDef[]
}

const props = defineProps<Props>()

const gridApi = shallowRef<GridApi>()
function onGridReady(params: { api: GridApi }) {
    gridApi.value = params.api
}

watch(
    () => props.loading,
    loading => {
        gridApi.value?.setGridOption("loading", loading)
    }
)

const gridOptions: GridOptions = {
    localeText: AG_GRID_LOCALE_CZ,
    autoSizeStrategy: {
        type: "fitGridWidth",
    },
    suppressHorizontalScroll: false,
    alwaysShowVerticalScroll: true,
    pagination: true,
    // paginationAutoPageSize: true,
    paginationPageSizeSelector: [20, 50, 100, 500, 1000],
    paginationPageSize: 50,
    domLayout: "normal",
    enableCellTextSelection: true,
    ensureDomOrder: true,
}
</script>

<style>
body {
    --ag-data-font-size: 12px;
    --ag-font-family: "unitprolight";
    --ag-column-border: solid 1px var(--color-border-gray);
    --ag-odd-row-background-color: #fcfcfc;

    --ag-spacing: 4px;
    --ag-list-item-height: 20px;

    --ag-icon-font-display: none;

    --ag-icon-image-filter: url("https://api.iconify.design/prime:filter.svg");
    --ag-icon-image-next: url("https://api.iconify.design/prime:angle-right.svg");
    --ag-icon-image-previous: url("https://api.iconify.design/prime:angle-left.svg");
    --ag-icon-image-last: url("https://api.iconify.design/prime:angle-double-right.svg");
    --ag-icon-image-first: url("https://api.iconify.design/prime:angle-double-left.svg");
    --ag-icon-image-small-up: url("https://api.iconify.design/prime:angle-up.svg");
    --ag-icon-image-small-down: url("https://api.iconify.design/prime:angle-down.svg");
    --ag-icon-image-asc: url("https://api.iconify.design/prime:arrow-up.svg");
    --ag-icon-image-desc: url("https://api.iconify.design/prime:arrow-down.svg");
}
.ag-center-cols-viewport {
    min-height: unset !important;
}
.ag-cell-wrapper {
    height: 100%;
}
.ag-cell-value {
    height: 100%;
}
.ag-paging-number,
.ag-paging-row-summary-panel-number {
    font-weight: 800;
}
</style>
